<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import {
  makeItemsProps,
  makeTagProps,
  makeDisabledProps,
  makeInvertColor,
  makeNestedProps,
  useNested,
  useItems,
  useDisabled,
  useInvertColor,
  useRender,
  makeThemeProps,
  provideTheme
} from '../../composable';

import { createList, createChildren, useFocus } from './composable';

import type { PropType } from 'vue';

export const componentName = 'CdsList';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Количество строк в описании элемента списка
     */
    lines: {
      type: [Boolean, String] as PropType<'one' | 'two' | 'three' | false>,
      default: 'one'
    },
    ...makeItemsProps(),
    ...makeTagProps(),
    ...makeDisabledProps(),
    ...makeInvertColor(),
    ...makeNestedProps({
      selectStrategy: 'single-leaf' as const,
      openStrategy: 'list' as const
    }),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие выбора элемента списка, возвращает массив id выбранных элементов списка
     * @type { unknown[] } value
     */
    'update:selected': (value: unknown[]) => true,
    /**
     * Событие открытия элемента списка, возвращает массив id открытых элементов списка
     * @type { unknown[] }
     */
    'update:opened': (value: unknown[]) => true,
    /**
     * Событие открытия элемента списка по клику, возвращает id элемента и его состояние
     * @type {{ id: unknown; value: boolean; path: unknown[], event: Event }}
     */
    'click:open': (value: { id: unknown; value: boolean; path: unknown[]; event: Event }) => true,
    /**
     * Событие выбора элемента списка по клику, возвращает id элемента и его состояние
     * @type {{ id: unknown; value: boolean; path: unknown[], event: Event }}
     */
    'click:select': (value: { id: unknown; value: boolean; path: unknown[]; event: Event }) => true
  },
  setup(props, context) {
    const { items } = useItems(props);
    const { themeClasses } = provideTheme(props);
    const { disabledClasses } = useDisabled(props);
    const { invertColorClasses } = useInvertColor(props, componentName);
    const { open, select } = useNested(props, context);
    const { contentRef, onFocus, onFocusin, onFocusout, onKeydown, focus } = useFocus();
    const lineClasses = computed(() => (props.lines ? `cds-list--lines-${props.lines}` : undefined));

    createList();

    useRender(() => {
      const children = createChildren({ ...props, items: unref(items) }, context);

      return h(
        props.tag,
        {
          ref: contentRef,
          role: 'listbox',
          class: [
            'cds-list',
            unref(lineClasses),
            unref(invertColorClasses),
            unref(disabledClasses),
            unref(themeClasses)
          ],
          onFocus,
          onFocusin,
          onFocusout,
          onKeydown
        },
        /**
         * @slot Элементы или группы списка
         * @type CdsListItem | CdsListGroup
         */
        children()
      );
    });

    context.expose({
      open,
      select,
      focus
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsList';
</style>
